import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArticleMarkdown } from './ArticleMarkdown';
import { useTranslation, formatLongDate } from '@structura/i18n';
import './ArticleView.css';

interface StructuraBitMetadata {
  slug: string;
  title: string;
  readingTime: string;
  date: string;
  thumbnail: string;
  category: string;
  author: string;
  description: string;
}

interface StructuraBitContent {
  metadata: StructuraBitMetadata;
  content: string;
}

// The outcome of one (slug, language) request. Keyed so a result can only ever be shown for the
// request the page currently wants: switching language means a new key, so an older response —
// success or failure — is simply not the current one.
interface LoadedArticle {
  key: string;
  article: StructuraBitContent | null;
  error: string;
}

const ArticleView: React.FC = () => {
  const { t, language } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const requestKey: string = `${slug ?? ''}|${language}`;
  const [loaded, setLoaded] = useState<LoadedArticle | null>(null);

  useEffect(() => {
    if (!slug) {
      return;
    }
    // One live request per key: a newer request aborts the older one, and a response that still
    // arrives after cleanup is ignored.
    const controller: AbortController = new AbortController();
    let active: boolean = true;
    axios.get<StructuraBitContent>(`/api/StructuraBits/${slug}?language=${language}`, { signal: controller.signal })
      .then((response): void => {
        if (active) {
          setLoaded({ key: requestKey, article: response.data, error: '' });
        }
      })
      .catch((err: unknown): void => {
        if (!active || axios.isCancel(err)) {
          return;
        }
        console.error('Error loading article:', err);
        setLoaded({ key: requestKey, article: null, error: t('bits.article.errLoad') });
      });
    return (): void => {
      active = false;
      controller.abort();
    };
  }, [slug, language, t, requestKey]);

  const current: LoadedArticle | null = loaded !== null && loaded.key === requestKey ? loaded : null;
  const loading: boolean = current === null;
  const error: string = current === null ? '' : current.error;
  const article: StructuraBitContent | null = current === null ? null : current.article;

  const formatDate = (dateString: string): string => formatLongDate(dateString, language);

  const handleBackClick = (): void => {
    navigate('/structura-bits');
  };

  if (loading) {
    return (
      <div className="article-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{t('bits.article.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="article-container">
        <div className="error-message">
          <h2>{t('bits.article.notFound')}</h2>
          <p>{error}</p>
          <button onClick={handleBackClick} className="back-button">
            {t('bits.article.backArrow')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="article-container" lang={language}>
      {/* Back Button */}
      <button onClick={handleBackClick} className="back-button-top">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
        {t('bits.article.back')}
      </button>

      {/* Article Header */}
      <header className="article-header">
        <div className="article-badge">
          <span className="sparkle-small">✨</span>
          Structura-Bit
        </div>
        <div className="article-category">{article.metadata.category}</div>
        <h1 className="article-title">{article.metadata.title}</h1>
        <div className="article-meta-bar">
          <span className="article-author">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            {article.metadata.author}
          </span>
          <span className="article-reading-time">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            {article.metadata.readingTime}
          </span>
          <span className="article-date">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            {formatDate(article.metadata.date)}
          </span>
        </div>
      </header>

      {/* Article Content */}
      <article className="article-content">
        <ArticleMarkdown content={article.content} labels={{ equation: t('bits.article.equation'), table: t('bits.article.table'), scrollHint: t('bits.article.scrollHint') }} />
      </article>

      {/* Footer */}
      <footer className="article-footer">
        <button onClick={handleBackClick} className="back-button">
          {t('bits.article.backArrow')}
        </button>
        <div className="article-footer-note">
          <em>{t('bits.article.footerNote')}</em>
        </div>
      </footer>
    </div>
  );
};

export default ArticleView;
